import React, {
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
} from "react";

type Loader<P> = () => Promise<{ default: ComponentType<P> }>;

type DynamicOptions<P> = {
  loading?: () => React.ReactNode;
  ssr?: boolean;
};

/**
 * Minimal `next/dynamic` compatibility using `React.lazy` + `Suspense`.
 */
export default function dynamic<P extends object>(
  loader: Loader<P>,
  options?: DynamicOptions<P>,
): LazyExoticComponent<ComponentType<P>> {
  const Lazy = React.lazy(loader);
  const fallback = options?.loading?.() ?? null;

  function DynamicComponent(props: P) {
    return (
      <Suspense fallback={fallback}>
        <Lazy {...props} />
      </Suspense>
    );
  }

  DynamicComponent.displayName = "DynamicComponent";
  return DynamicComponent as unknown as LazyExoticComponent<ComponentType<P>>;
}
